import { ComponentProps } from 'react';
import { useIntl } from 'react-intl';
import {
  IconAlertOctagon,
  IconAlertTriangle,
  IconFileTypeZip,
  IconLock,
} from '@rossum/ui/icons/tabler';
import { SvgIcon, Tooltip } from '@rossum/ui/material';
import type { Prettify } from '../../lib/typeUtils';
import { ParsedAttachmentStatus } from '../../types/attachmentStatus';
import { TaskProgress } from './components/TaskProgress';
import { UnprocessedDocument } from './TaskContext';

type UploadsTaskProgressProps = {
  task: UnprocessedDocument;
};

type StatusIcon = {
  label: 'extracted' | 'failed' | 'protected' | 'unprocessed';
  iconColor: ComponentProps<typeof SvgIcon>['color'];
  Icon: typeof IconAlertTriangle;
};

/** Statuses without an entry here fall back to `unknown`. */
const statusToIconMap: Prettify<
  Partial<Record<ParsedAttachmentStatus, StatusIcon>> & {
    unknown: StatusIcon;
  }
> = {
  uploads_failed: {
    label: 'failed',
    iconColor: 'error',
    Icon: IconAlertOctagon,
  },
  extracted_archive: {
    label: 'extracted',
    iconColor: 'success',
    Icon: IconFileTypeZip,
  },
  failed_to_extract: {
    label: 'failed',
    iconColor: 'error',
    Icon: IconAlertOctagon,
  },
  password_protected_archive: {
    label: 'protected',
    iconColor: 'error',
    Icon: IconLock,
  },
  filtered_by_hook_custom: {
    label: 'unprocessed',
    iconColor: 'warning',
    Icon: IconAlertTriangle,
  },
  filtered_by_queue_mime_type: {
    label: 'unprocessed',
    iconColor: 'warning',
    Icon: IconAlertTriangle,
  },
  filtered_by_insecure_mime_type: {
    label: 'unprocessed',
    iconColor: 'warning',
    Icon: IconAlertTriangle,
  },
  broken_image: { label: 'failed', iconColor: 'error', Icon: IconAlertOctagon },
  unknown: {
    label: 'unprocessed',
    iconColor: 'warning',
    Icon: IconAlertTriangle,
  },
};

export const UploadsTaskProgress = ({ task }: UploadsTaskProgressProps) => {
  const intl = useIntl();

  const { iconColor, Icon, label } =
    statusToIconMap[task.data.status] ?? statusToIconMap.unknown;

  return (
    <TaskProgress
      primaryLabel={
        task.data.type === 'uploads' && task.data.fileName
          ? task.data.fileName
          : intl.formatMessage({
              id: 'components.asyncTasksDialog.item.title.placeholder',
            })
      }
      secondaryLabel={
        task.data.type === 'uploads' &&
        task.data.queueName &&
        task.data.status === 'uploads_failed'
          ? intl.formatMessage(
              {
                id: `components.asyncTasksDialog.item.subtitle.upload.${task.data.status}`,
              },
              {
                queueName: task.data.queueName,
              }
            )
          : ''
      }
      progressLabel={intl.formatMessage({
        id: `components.asyncTasksDialog.item.progress.${label}`,
      })}
      progressIcon={
        <Tooltip title={task.data.detail ?? ''}>
          <SvgIcon color={iconColor} fontSize="medium">
            <Icon />
          </SvgIcon>
        </Tooltip>
      }
    />
  );
};
